import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useRegDetail } from "shared/context/regDetail";
import useNavigateWrapper from "shared/hooks/useNavigateWrapper";
import useScreenName from "apps/myExperience/hooks/useScreenName";
import { useParticipantDetails } from "apps/myExperience/context/participantDetails";
import { updateActivityListForAdminWithChanges, useGetAssociatedProgramsActivities } from "./dataloader";
import { associatedProgramBehaviourFactory } from "apps/console/modules/program/workflow/associateProgram/profile/activity/details/components/associatedProgramBehaviourFactory";
import { useCopies } from "shared/hooks/useCopies";
import { useEnabledFeatures } from "apps/myExperience/context/enabledFeatures";
import useLogAnalyticsScreenHook from "apps/myExperience/context/analyticsWrapper/hooks/useLogAnalyticsScreenHook";
import { AnalyticsScreenNameEnum } from "shared/enums/AnalyticsEnums";

export function useActivityListForActivityAdminService() {
  const { navigate, navigateBack } = useNavigateWrapper();
  const { programParticipantId } = useParams();
  const [searchParams] = useSearchParams();
  const copies = useCopies();
  const { isAITextEnhancementEnabled } = useEnabledFeatures();

  useLogAnalyticsScreenHook({
    properties: {
      screenType: AnalyticsScreenNameEnum.COMPLETE_PROFILE_ACTIVITY_LIST,
      programParticipantId
    }
  });
  const { id: registrationId } = useRegDetail();
  const { getProgramDetails, getEventDetails, refreshIfActionPendingInProgram } = useParticipantDetails();

  useScreenName(copies.get("activity.listing.header"));

  const { programId = "", eventId } = React.useMemo(() => {
    return {
      eventId: programParticipantId && getEventDetails(programParticipantId)?.id,
      programId: programParticipantId && getProgramDetails(programParticipantId)?.id
    };
  }, [programParticipantId, getEventDetails, getProgramDetails]);

  const editClickHandler = React.useCallback(
    (id: string) => {
      navigate({ pathname: id, search: searchParams.toString() });
    },
    [navigate, searchParams]
  );

  const { data: adminActivityList = [] } = useGetAssociatedProgramsActivities({ programParticipantId });

  const config = React.useMemo(() => {
    return associatedProgramBehaviourFactory();
  }, []);

  const updateListWithChangesHandler = React.useCallback(
    (queryClient, _, dataToUpdate, invalidateList) => {
      programParticipantId &&
        updateActivityListForAdminWithChanges(queryClient, programParticipantId, dataToUpdate, invalidateList);
      invalidateList && navigateBack();
    },
    [navigateBack, programParticipantId]
  );

  const refreshMyActionsOnUpdate = React.useCallback(
    () => refreshIfActionPendingInProgram(programId),
    [programId, refreshIfActionPendingInProgram]
  );

  return {
    config,
    eventId,
    programId,
    registrationId,
    editClickHandler,
    updateListWithChangesHandler,
    hasProgramWriteAuthority: true,
    refreshMyActionsOnUpdate,
    associatedProgramActivities: config.trasformActivitiesForActivityAdminResponse(adminActivityList),
    isAITextEnhancementEnabled
  };
}
